/**
 * Which declared payment methods actually settle an invoice.
 *
 * An invoice declares its payment methods to the MCF (DGI §2.22), and
 * CREDIT is a legitimate declaration: a sale on credit (« vente à terme »).
 * It moves no money, so it never counts toward `amountPaid`, and a later
 * receipt is recorded with the method the money actually came in by.
 */
/** Declared methods that leave the amount unpaid. */
export const NON_SETTLING_PAYMENT_METHODS = ["CREDIT"];
/** True when a payment made by this method reduces what the client owes. */
export function isSettlingPaymentMethod(method) {
    return !NON_SETTLING_PAYMENT_METHODS.includes(method);
}
/**
 * Method of the settlement-rounding row: the sub-franc difference between
 * the certified net and the whole francs received (see payable-amount.ts).
 * It moves no money, is never declared to the MCF and is never an input.
 */
export const ARRONDI_PAYMENT_METHOD = "ARRONDI";
/** Id of the first settlement-rounding row of an invoice: at most one per invoice. */
export function arrondiPaymentId(invoiceId) {
    return `arrondi-${invoiceId}`;
}
/**
 * Ids of the rounding rows a signed credit note writes on its original when
 * it changes the rounding the original's balance calls for: the row that
 * cancels the rounding in force (its `reference` is the id of the row it
 * cancels) and the row that carries the new rounding. One pair at most per
 * credit note.
 */
export function arrondiCreditNoteIds(creditNoteId) {
    return {
        cancellation: `arrondi-annule-${creditNoteId}`,
        replacement: `arrondi-avoir-${creditNoteId}`,
    };
}
/**
 * Ids of the rounding rows written when a settlement changes the rounding a
 * signed invoice's balance calls for: recorded after the rounding in force
 * was cancelled, or cancelled itself. Same roles as `arrondiCreditNoteIds`,
 * one pair at most per settlement.
 */
export function arrondiSettlementIds(settlementId) {
    return {
        cancellation: `arrondi-annule-reglement-${settlementId}`,
        replacement: `arrondi-reglement-${settlementId}`,
    };
}
/**
 * Id of the row that cancels the settlement `paymentId`: the negative of its
 * amount, in its method, its `reference` the id of the row it cancels. At
 * most one per settlement.
 */
export function settlementCancellationId(paymentId) {
    return `reglement-annule-${paymentId}`;
}
/** True for a row that cancels a settlement (`settlementCancellationId`). */
export function isSettlementCancellation(row) {
    return row.reference != null && row.id === settlementCancellationId(row.reference);
}
/** `rows` without the settlements a row cancels and without those rows: the settlements in force. */
export function settlementsInForce(rows) {
    const cancelled = new Set(rows.flatMap((row) => (isSettlementCancellation(row) ? [row.reference] : [])));
    return rows.filter((row) => !isSettlementCancellation(row) && !cancelled.has(row.id));
}
/** True for the settlement-rounding row. */
export function isArrondiPaymentMethod(method) {
    return method === ARRONDI_PAYMENT_METHOD;
}
/**
 * True when a payment made by this method is money actually received: it
 * settles (not CREDIT) and is not the rounding row. `amountPaid`, cash and
 * the MCF declaration count these.
 */
export function isReceivedPaymentMethod(method) {
    return isSettlingPaymentMethod(method) && !isArrondiPaymentMethod(method);
}
//# sourceMappingURL=settlement.js.map